import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  UNLOCK_PRICE,
  emptyStore,
  ensureRoleAccess,
  loadEmployer,
  roleAccessState,
  type Application,
  type EmployerJob,
  type EmployerStore,
} from "@/components/site/employer";
import { fetchEmployerWorkspace, getEmployerSession } from "@/lib/employer-api";
import { AccessPill } from "@/components/site/RoleAccess";

export const Route = createFileRoute("/upgrade")({
  validateSearch: (search: Record<string, unknown>) => ({
    job: typeof search["job"] === "string" ? (search["job"] as string) : undefined,
  }),
  head: () => {
    const t = "Unlock your sales talent | CLOSDD";
    const d =
      "Unlock the salespeople CLOSDD has already brought to your role. Hear their pitch, see their full profile and contact the candidates you want.";
    return {
      meta: [
        { name: "robots", content: "noindex,nofollow" },
        { title: t },
        { name: "description", content: d },
        { property: "og:title", content: t },
        { property: "og:description", content: d },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  component: UpgradePage,
});

const btn =
  "inline-flex items-center justify-center rounded-full border border-white/20 bg-purple-strong px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-white shadow-[0_14px_40px_-14px_rgba(16,48,107,0.55)] transition-colors hover:bg-purple-mid";
const label = "text-[12px] font-bold uppercase tracking-[0.28em] text-ink/45";
const labelLight = "text-[12px] font-bold uppercase tracking-[0.28em] text-mint-soft";

function yearsValue(years: string) {
  const m = years.match(/(\d+)/);
  return m && m[1] ? parseInt(m[1], 10) : 0;
}

function candidateMatches(app: Application) {
  // Lightweight proxy for "matches your requirements" until the matching engine is live.
  return yearsValue(app.years) >= 3 && app.voiceAnswers >= 3;
}

function roleStats(store: EmployerStore, job: EmployerJob) {
  const apps = store.applications.filter((a) => a.jobId === job.id);
  const completedVoice = apps.filter((a) => a.voiceAnswers >= 3).length;
  const matched = apps.filter(candidateMatches).length;
  return {
    applied: apps.length,
    completedVoice,
    matched,
  };
}


function ValueCard({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="group relative flex gap-4 rounded-2xl border border-purple-strong/10 bg-white p-5 transition hover:-translate-y-0.5 hover:border-purple-strong/25 hover:shadow-[0_18px_50px_-30px_rgba(16,48,107,0.35)]">
      <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-purple-soft text-purple-strong transition group-hover:bg-purple-strong group-hover:text-white">
        {icon}
      </div>
      <div>
        <h3 className="font-serif text-[16px] leading-[1.2] tracking-[-0.015em] text-ink">{title}</h3>
        <p className="mt-2 text-[15px] leading-[1.65] text-ink/55">{body}</p>
      </div>
    </div>
  );
}

function Stat({ value, caption }: { value: number; caption: string }) {
  return (
    <div className="flex-1 px-5 py-5 text-center sm:text-left">
      <p className="font-serif text-[28px] leading-none tracking-[-0.02em] text-white sm:text-[32px]">{value}</p>
      <p className="mt-2 text-[12px] font-bold uppercase leading-[1.5] tracking-[0.14em] text-white/50">{caption}</p>
    </div>
  );
}

function LockedCandidate({ app, index }: { app: Application; index: number }) {
  const first = app.name.split(" ")[0] ?? "Candidate";
  const initial = (app.name.split(" ")[1] ?? "?").charAt(0).toUpperCase();
  return (
    <div
      className="flex items-center gap-4 border-b border-purple-strong/[0.07] px-5 py-4 last:border-0"
      style={{ opacity: 1 - index * 0.14 }}
    >
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-purple-soft text-[13px] font-semibold text-purple-strong">
        {first.charAt(0)}
        {initial}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14.5px] font-semibold text-ink">
          {first} <span className="select-none blur-[5px]">{initial}astings</span>
        </p>
        <p className="mt-0.5 truncate text-[12.5px] text-ink/50">
          {app.years} · {app.location} ·{" "}
          <span className="select-none blur-[4px]">{app.email}</span>
        </p>
      </div>
      <div className="hidden items-center gap-2 sm:flex">
        {app.hasPitch ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Pitch ready
          </span>
        ) : null}
        <span className="inline-flex items-center gap-1.5 rounded-full bg-ink/[0.06] px-2.5 py-1 text-[11px] font-semibold text-ink/50">
          <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V8a4 4 0 0 1 8 0v3" />
          </svg>
          Locked
        </span>
      </div>
    </div>
  );
}

function UpgradePage() {
  const { job: jobParam } = Route.useSearch();
  const [store, setStore] = useState<EmployerStore>(emptyStore);

  useEffect(() => {
    (async () => {
      const user = await getEmployerSession();
      if (user) {
        const s = await fetchEmployerWorkspace().catch(() => emptyStore);
        setStore(s);
        return;
      }
      const s = loadEmployer();
      setStore(s.jobs.length ? ensureRoleAccess(s.jobs.map((j) => j.id)) : s);
    })();
  }, []);

  const roles = store.jobs.filter((j) => !j.exclusive);
  const locked = roles.filter((j) => !roleAccessState(store, j.id).unlocked);
  const focus =
    (jobParam ? roles.find((j) => j.id === jobParam) : undefined) ?? locked[0] ?? roles[0];
  const focusState = focus ? roleAccessState(store, focus.id) : null;
  const stats = focus ? roleStats(store, focus) : { applied: 0, completedVoice: 0, matched: 0 };
  const nextJob = focusState && !focusState.unlocked ? focus : locked[0];
  const otherLocked = locked.filter((j) => j.id !== focus?.id);
  const focusApps = focus ? store.applications.filter((a) => a.jobId === focus.id) : [];

  const unlockCta = nextJob ? (
    <Link to="/checkout" search={{ job: nextJob.id }} className={`${btn} w-full`}>
      Unlock My Candidates — ${UNLOCK_PRICE}
    </Link>
  ) : (
    <Link to="/employer" className={`${btn} w-full`}>
      Go to your candidates
    </Link>
  );

  return (
    <div className="min-h-screen bg-[#f6f8fc]">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-purple-strong/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-[1180px] items-center justify-between gap-6 px-5 py-3.5">
          <Link to="/" className="font-serif text-[20px] font-bold uppercase leading-none tracking-[0.04em] text-white">
            closdd
          </Link>
          <div className="flex items-center gap-5">
            <Link
              to="/employer"
              className="rounded-full bg-white/10 px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.14em] text-white transition hover:bg-white/15"
            >
              Back to console
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-purple-strong pb-14 pt-16 sm:pb-20 sm:pt-20">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-[15%] -top-[20%] h-[520px] w-[520px] rounded-full bg-purple-mid/25 blur-[130px]" />
          <div className="absolute -right-[12%] top-[10%] h-[420px] w-[420px] rounded-full bg-purple-strong/40 blur-[120px]" />
        </div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:64px_64px] opacity-25" />

        <div className="relative mx-auto w-full max-w-[1180px] px-5">
          <div className="grid items-end gap-12 lg:grid-cols-[1.15fr_0.85fr]">
            <div>
              <p className={labelLight}>Unlock your sales talent</p>
              <h1 className="mt-6 max-w-[680px] font-serif text-[28px] leading-[0.98] tracking-[-0.03em] text-white sm:text-[62px]">
                {focus ? (
                  <>
                    {stats.applied || "Real"} salespeople are waiting behind{" "}
                    <span className="text-purple-soft">{focus.title}</span>.
                  </>
                ) : (
                  <>Real salespeople are waiting behind your role.</>
                )}
              </h1>
              <p className="mt-7 max-w-[560px] text-[16px] leading-[1.7] text-white/65">
                You&apos;re not paying to view applications. You&apos;re unlocking access to the
                salespeople CLOSDD has already brought to your role — pitches you can hear, people
                you can call.
              </p>
              {focus && focusState ? (
                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <AccessPill state={focusState} />
                </div>
              ) : null}

            </div>

            <div className="rounded-[22px] border border-white/12 bg-white/[0.04] backdrop-blur">
              <div className="flex flex-col divide-y divide-white/10 sm:flex-row sm:divide-x sm:divide-y-0">
                <Stat value={stats.applied} caption="salespeople have applied" />
              </div>
              <div className="border-t border-white/10 px-5 py-4">
                <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-white/75">
                  Unlock to see who&apos;s behind them.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="mx-auto w-full max-w-[1180px] px-5 pb-20">
        <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="mt-10 space-y-8">
            {/* Locked candidate stack */}
            {focusApps.length ? (
              <section className="overflow-hidden rounded-2xl border border-purple-strong/10 bg-white shadow-[0_30px_80px_-60px_rgba(13,27,46,0.45)]">
                <div className="flex items-center justify-between gap-4 border-b border-purple-strong/10 px-5 py-4">
                  <p className={label}>Who applied</p>
                  <p className="text-[12.5px] font-semibold text-ink/45">
                    {focusApps.length} candidate{focusApps.length === 1 ? "" : "s"}
                  </p>
                </div>
                <div>
                  {focusApps.slice(0, 4).map((app, i) => (
                    <LockedCandidate key={app.id} app={app} index={i} />
                  ))}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-purple-strong/10 bg-[#f8fafd] px-5 py-4">
                  <p className="text-[13px] text-ink/55">
                    Names, CVs and contact details are hidden until you unlock this role.
                  </p>
                  {nextJob ? (
                    <Link
                      to="/checkout"
                      search={{ job: nextJob.id }}
                      className="text-[13px] font-semibold text-purple-strong transition hover:text-purple-mid"
                    >
                      Unlock now →
                    </Link>
                  ) : null}
                </div>
              </section>
            ) : null}

            <section>
              <p className={label}>What unlocking gives you</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <ValueCard
                  icon={
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                      <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
                    </svg>
                  }
                  title="Hear how they sell"
                  body="Listen to their 60-second voice assessment and hear their communication, confidence and sales ability before you decide who to speak to."
                />
                <ValueCard
                  icon={
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.56 12.56 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.56 12.56 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  }
                  title="Contact the candidates you want"
                  body="Get their direct contact details and approach the people you want to interview."
                />
                <div className="sm:col-span-2">
                  <ValueCard
                    icon={
                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M13 2 4.09 12.11a2.5 2.5 0 0 0 .27 3.5l.66.58a2.5 2.5 0 0 0 3.5-.27L20 6" />
                        <path d="M10.5 6.5 16 2l1 5.5" />
                        <path d="M18 13v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7" />
                      </svg>
                    }
                    title="Move faster"
                    body="Instead of waiting for applications and spending hours going through CVs, start with salespeople who have already raised their hand for the opportunity."
                  />
                </div>
              </div>
            </section>

            <section className="rounded-[22px] border border-purple-strong/10 bg-white px-6 py-6 sm:px-8 sm:py-7">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className={label}>100% Money-Back Guarantee</p>
                  <h3 className="mt-3 font-serif text-[20px] leading-[1.1] tracking-[-0.025em] text-ink sm:text-[25px]">
                    If you unlock this role and don&apos;t find a candidate worth speaking to,
                    we&apos;ll refund your ${UNLOCK_PRICE}.
                  </h3>
                  <p className="mt-3 max-w-[520px] text-[16px] leading-[1.65] text-ink/55">
                    No risk. No awkward conversations. If the talent isn&apos;t right, you get your money back.
                  </p>
                </div>
                <div className="shrink-0">
                  <svg viewBox="0 0 64 64" className="h-14 w-14 text-purple-strong" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="32" cy="32" r="28" />
                    <path d="M20 32l8 8 16-16" />
                  </svg>
                </div>
              </div>
            </section>

            {otherLocked.length ? (
              <section className="rounded-2xl border border-purple-strong/10 bg-white px-6 py-5">
                <p className={label}>Your other roles</p>
                <div className="mt-3 divide-y divide-ink/10">
                  {otherLocked.map((job) => {
                    const state = roleAccessState(store, job.id);
                    const count = store.applications.filter((a) => a.jobId === job.id).length;
                    return (
                      <div key={job.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                        <div className="min-w-0">
                          <p className="truncate text-[14.5px] font-semibold text-ink">{job.title}</p>
                          <p className="mt-0.5 text-[13px] text-ink/50">
                            {count} applicant{count === 1 ? "" : "s"} · {state.label}
                          </p>
                        </div>
                        <Link
                          to="/upgrade"
                          search={{ job: job.id }}
                          className="text-[13px] font-semibold text-purple-strong transition hover:text-purple-mid"
                        >
                          Unlock this role
                        </Link>
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : null}
          </div>

          {/* Sticky pricing panel */}
          <div className="lg:mt-10">

            <div className="lg:sticky lg:top-24">
              <div className="overflow-hidden rounded-[22px] border border-purple-strong/10 bg-white shadow-[0_40px_100px_-60px_rgba(13,27,46,0.5)]">
                <div className="border-b border-purple-strong/10 bg-purple-strong px-7 py-7">
                  <p className={labelLight}>Unlock this role</p>
                  <p className="mt-4 font-serif text-[38px] leading-none tracking-[-0.035em] text-white">
                    ${UNLOCK_PRICE}
                  </p>
                  <p className="mt-3 text-[15px] leading-[1.6] text-white/55">
                    One-off payment. This role only. No subscription, no fee per hire.
                  </p>
                </div>
                <div className="px-7 py-7">
                  {focus ? (
                    <p className="text-[12px] font-bold uppercase tracking-[0.16em] leading-[1.5] text-ink">
                      {focus.title}
                      <span className="ml-1 font-bold text-ink/45">
                        · {stats.applied} applicant{stats.applied === 1 ? "" : "s"}
                      </span>
                    </p>
                  ) : null}
                  <ul className="mt-4 space-y-2.5">
                    {[
                      "Full candidate names and profiles",
                      "Direct email and phone contact",
                      "Shortlist and message candidates",
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-[15px] leading-[1.6] text-ink/65">
                        <svg viewBox="0 0 24 24" className="mt-0.5 h-4 w-4 shrink-0 text-purple-strong" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m5 12 4.5 4.5L19 7" />
                        </svg>
                        {item}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 space-y-3">{unlockCta}</div>
                  <p className="mt-5 text-center text-[11px] font-bold uppercase tracking-[0.16em] text-ink/40">
                    Voice pitches stay free — always.
                  </p>
                </div>
              </div>

              <div className="mt-4 rounded-[18px] border border-purple-strong/15 bg-purple-soft/60 px-6 py-5">
                <p className="font-serif text-[17px] leading-[1.25] tracking-[-0.015em] text-purple-strong">
                  Your role already has salespeople interested in it. Don&apos;t leave them sitting
                  in your inbox.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <section className="relative overflow-hidden bg-purple-strong py-16 sm:py-24">
        <div className="pointer-events-none absolute inset-0 opacity-60">
          <div className="absolute -left-[20%] top-0 h-[420px] w-[420px] rounded-full bg-purple-strong/25 blur-[120px]" />
          <div className="absolute -right-[10%] bottom-0 h-[360px] w-[360px] rounded-full bg-purple-mid/20 blur-[100px]" />
        </div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:64px_64px] opacity-20" />

        <div className="relative mx-auto w-full max-w-[760px] px-5 text-center">
          <p className={labelLight}>Hire faster</p>
          <h2 className="mt-5 font-serif text-[26px] leading-[1.02] tracking-[-0.03em] text-white sm:text-[54px]">
            Stop searching through CVs. Hear real salespeople, see their experience, and find your
            next hire faster.
          </h2>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            {nextJob ? (
              <Link to="/checkout" search={{ job: nextJob.id }} className={btn}>
                Unlock My Candidates — ${UNLOCK_PRICE}
              </Link>
            ) : (
              <Link to="/employer" className={btn}>
                Go to your candidates
              </Link>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
